import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', variant = 'default', size = 'default', children, ...props }, ref) => {
    const variantStyles = {
      // Primary: Solid white on dark bg - for main actions
      primary: 'bg-white text-black hover:bg-gray-100',
      // Default: Indigo accent - for secondary emphasis
      default: 'bg-indigo-600 text-white hover:bg-indigo-700',
      // Outline: Border only - for tertiary actions
      outline: 'border border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 hover:text-white',
      // Ghost: No border, minimal - for subtle actions
      ghost: 'text-gray-400 hover:bg-gray-800 hover:text-white',
      // Destructive: Red - for dangerous actions
      destructive: 'bg-red-600 text-white hover:bg-red-700',
      // Secondary: Dark gray - for non-primary actions
      secondary: 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white',
      // Link: Text only - inline links
      link: 'text-indigo-400 underline-offset-4 hover:underline hover:text-indigo-300',
    };

    const sizeStyles = {
      default: 'h-10 px-4 py-2',
      sm: 'h-8 px-3 text-xs',
      lg: 'h-11 px-8',
      icon: 'h-10 w-10',
    };

    return (
      <button
        ref={ref}
        className={`inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:pointer-events-none disabled:opacity-50 ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
